/**
 * Tiny client for the public award event/stats endpoints.
 * No auth, no React Query — fire-and-forget beacons.
 */

const API_HOST = (() => {
  if (typeof window === 'undefined') return 'https://api.badges.ninja';
  return window.location.hostname.includes('staging')
    ? 'https://api-staging.badges.ninja'
    : 'https://api.badges.ninja';
})();

export type AwardEvent = 'view' | 'share' | 'download' | 'click_linkedin_addtoprofile' | 'click_pdf' | 'click_vc' | 'click_verify';
export type ShareNetwork = 'linkedin' | 'twitter' | 'facebook' | 'email' | 'whatsapp' | 'telegram' | 'copy';

// The award API is not behind CloudFront, so it can't read CloudFront-Viewer-Country.
// The viewer's IANA timezone is a no-cost, no-PII proxy the server maps to a country
// (src/@common/tz-country.mjs) to power the geography chart.
function viewerTz(): string {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch { return ''; }
}

export function fireAwardEvent(guid: string, event: AwardEvent, network?: ShareNetwork): void {
  if (!guid) return;
  const url = `${API_HOST}/certify-badge/award/${encodeURIComponent(guid)}/event`;
  const tz = viewerTz();
  const payload = JSON.stringify({ event, ...(network ? { network } : {}), ...(tz ? { tz } : {}) });
  // NB: do NOT use navigator.sendBeacon here — it always sends credentials
  // (cookies), which turns this into a credentialed cross-origin request. The
  // API's managed CORS doesn't set Access-Control-Allow-Credentials, so the
  // preflight fails ("...Allow-Credentials header ... must be 'true'") and the
  // beacon is dropped with a console error on every award view. A `keepalive`
  // fetch with `credentials: 'omit'` gives the same unload-safety without the
  // credentialed-CORS requirement. Fire-and-forget; failures are swallowed.
  try {
    void fetch(url, {
      method: 'POST',
      body: payload,
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      credentials: 'omit',
      mode: 'cors',
    }).catch(() => {});
  } catch { /* ignore — analytics must never affect the page */ }
}

export interface AwardStats {
  totals: Partial<Record<AwardEvent, number>>;
  networks: Partial<Record<AwardEvent, Partial<Record<ShareNetwork, number>>>>;
}

export async function fetchAwardStats(guid: string): Promise<AwardStats> {
  if (!guid) return { totals: {}, networks: {} };
  try {
    const r = await fetch(`${API_HOST}/certify-badge/award/${encodeURIComponent(guid)}/stats`);
    if (!r.ok) return { totals: {}, networks: {} };
    return await r.json();
  } catch {
    return { totals: {}, networks: {} };
  }
}
